import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import PageBreadcrumbs from "@insite/content-library/Components/PageBreadcrumbs";
import Page from "@insite/mobius/Page";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomSettingCollectionModel } from "@NA/Store/CustomTypeModel/CustomSettingCollectionModel";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import { isCoveoSearchEnabled } from "@NA/Utilities/CoveoUtilities";
import * as React from "react";
import { ReactNode } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import CoveoProvider from "@NA/Components/Coveo/Providers/CoveoProvider";
import loadCoveoEngine from "@NA/Handlers/Coveo/CoveoEngine";
import loadCoveoStaticState from "@NA/Handlers/Coveo/CoveoLoadStaticState";
import { getPath, hasEngine, isPlp, isSearch } from "@NA/Utils/Coveo/CoveoUtils";
import CoveoParameterManager from "@NA/Components/Coveo/CoveoParameterManager";
import CoveoCartItems from "@NA/Components/Coveo/CoveoCartItems";

interface PageLayoutProps {
    showHeader: boolean;
    header: ReactNode;
    showBreadcrumbs: boolean;
    pageContent: ReactNode;
    showFooter: boolean;
    footer: ReactNode;
}

export interface PageLayoutStyles {
    breadcrumbs?: InjectableCss;
}

export const pageLayoutStyles: PageLayoutStyles = {
    breadcrumbs: {
        css: css`
            height: 32px;
            @media only screen and (max-width: 767px) {
                height: unset;
            }
        `,
    },
};

const mapStateToProps = (state: CustomApplicationState) => {
    return {
        isCoveoUser: state.context?.session?.userRoles?.includes("Coveo-Access") ?? false,
        settingsCollection: getSettingsCollection(state) as unknown as CustomSettingCollectionModel,
        coveoCommerce: state.ADIGlobalData.coveoCommerce,
        isListingPage: isPlp(state) || isSearch(state),
        path: getPath(state),
    };
};

const mapDispatchToProps = {
    loadCoveoEngine,
    loadCoveoStaticState,
};

type Props = PageLayoutProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

class PageLayout extends React.Component<Props> {
    UNSAFE_componentWillMount(): void {
        const { coveoCommerce, loadCoveoEngine, isCoveoUser, settingsCollection } = this.props;
        const isCoveoEnabled = isCoveoSearchEnabled(settingsCollection, isCoveoUser);

        if (isCoveoEnabled && !coveoCommerce?.isEngineLoading && !hasEngine(coveoCommerce)) {
            loadCoveoEngine();
        }
    }

    componentDidUpdate(prevProps: Props): void {
        const { coveoCommerce, path, loadCoveoStaticState, isCoveoUser, settingsCollection } = this.props;
        const isCoveoEnabled = isCoveoSearchEnabled(settingsCollection, isCoveoUser);

        if (
            isCoveoEnabled &&
            hasEngine(coveoCommerce) &&
            path.toLowerCase() !== prevProps.path.toLowerCase() &&
            !coveoCommerce.isStaticStateLoading
        ) {
            loadCoveoStaticState({});
        }
    }

    render() {
        const {
            showHeader,
            header,
            showBreadcrumbs,
            pageContent,
            showFooter,
            footer,
            isCoveoUser,
            settingsCollection,
            isListingPage,
        } = this.props;

        const isCoveoEnabled = isCoveoSearchEnabled(settingsCollection, isCoveoUser);
        const content = (
            <>
                {showHeader && header}
                {showBreadcrumbs && (
                    <Page as="div">
                        <StyledWrapper {...pageLayoutStyles.breadcrumbs}>
                            <PageBreadcrumbs />
                        </StyledWrapper>
                    </Page>
                )}
                {isCoveoEnabled ? (
                    <CoveoProvider>
                        {isListingPage && <CoveoParameterManager />}
                        <CoveoCartItems />
                        {pageContent}
                    </CoveoProvider>
                ) : (
                    pageContent
                )}
                {showFooter && footer}
            </>
        );

        return content;
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(PageLayout);
